import { AppointmentQuestionnaire } from "@/features/appointments/AppointmentQuestionnaire";
import { type BokaQuestionnaireData } from "@/features/project/useProjectQuestionnaire";
import { type GroupQuestions, type hfb, useQuestionnaire } from "@repo/api";
import { Tab, TabPanel, Tabs, Text } from "@repo/skapa";
import { t } from "@repo/translation";
import { Line, SectionContainer, useSettings } from "@repo/ui";
import { Fragment, useEffect } from "react";

import { QuestionItem } from "./QuestionItem";
import { filterGroups, getTranslation } from "./utils";

export const BOKA_TAB_KEY = "BOKA";

export interface QuestionnaireGroupProps {
  selectedHfbs: hfb[];
  service?: string;
  currentTab?: string;
  setCurrentTab?: (tab: string) => void;
  showB2BTab?: boolean;
  bokaQuestionnaire?: BokaQuestionnaireData;
}

export const QuestionnaireGroup = ({
  selectedHfbs,
  service,
  currentTab,
  setCurrentTab,
  showB2BTab,
  bokaQuestionnaire,
}: QuestionnaireGroupProps) => {
  const { market, language } = useSettings();
  const settings = useQuestionnaire(market, language);
  const showBokaTab = bokaQuestionnaire !== undefined;

  const filteredGroups = filterGroups(
    settings?.need_analysis?.groups ?? [],
    selectedHfbs,
    service,
    showB2BTab
  );

  useEffect(() => {
    const validTabs = [
      ...(showBokaTab ? [BOKA_TAB_KEY] : []),
      ...filteredGroups.map((group) => group.group_name_key),
    ];

    if (!setCurrentTab || validTabs.length === 0) {
      return;
    }

    const validCurrentTab = validTabs.includes(String(currentTab));

    if (!validCurrentTab) {
      setCurrentTab(validTabs[0]);
    }
  }, [filteredGroups, currentTab, setCurrentTab, showBokaTab]);

  if (!settings) {
    return null;
  }

  if (filteredGroups.length === 0 && !showBokaTab) {
    return <Text>{t("label.no_questions_available")}</Text>;
  }

  return (
    <SectionContainer>
      <Tabs
        activeTab={currentTab ? `group-tab-panel-${currentTab}` : undefined}
        tabs={[
          ...(showBokaTab
            ? [
                <Tab
                  key={`group-tab-${BOKA_TAB_KEY}`}
                  text={t("label.appointments")}
                  tabPanelId={`group-tab-panel-${BOKA_TAB_KEY}`}
                  onClick={() => setCurrentTab?.(BOKA_TAB_KEY)}
                />,
              ]
            : []),
          ...filteredGroups.map((group) => (
            <Tab
              key={`group-tab-${group.group_name_key}`}
              text={getTranslation(
                group.group_translation,
                language,
                group.group_name_key
              )}
              tabPanelId={`group-tab-panel-${group.group_name_key}`}
              onClick={() => setCurrentTab?.(group.group_name_key)}
            />
          )),
        ]}
        tabPanels={[
          ...(showBokaTab
            ? [
                <TabPanel
                  key={`group-tab-panel-${BOKA_TAB_KEY}`}
                  tabPanelId={`group-tab-panel-${BOKA_TAB_KEY}`}
                >
                  <AppointmentQuestionnaire {...bokaQuestionnaire} />
                </TabPanel>,
              ]
            : []),
          ...filteredGroups.map((group) => (
            <TabPanel
              key={`group-tab-panel-${group.group_name_key}`}
              tabPanelId={`group-tab-panel-${group.group_name_key}`}
            >
              <SectionContainer>
                {group.questions?.map((question: GroupQuestions) => {
                  const templateQuestion = settings.templates?.questions?.find(
                    (tempQ) =>
                      tempQ.question_id_key === question.question_id_key
                  );

                  if (templateQuestion) {
                    return (
                      <Fragment
                        key={`${group.group_name_key}-${question.question_id_key}`}
                      >
                        <Text textStyle="Heading.XS" wordBreakWord>
                          {getTranslation(
                            templateQuestion.question_translation,
                            language,
                            templateQuestion.question_name
                          )}
                        </Text>
                        <SectionContainer key={question.question_id_key}>
                          <QuestionItem
                            question={templateQuestion}
                            isB2B={group.group_name_key === "B2B"}
                          />
                          <Line />
                        </SectionContainer>
                      </Fragment>
                    );
                  }
                  return null;
                })}
              </SectionContainer>
            </TabPanel>
          )),
        ]}
      />
    </SectionContainer>
  );
};
